/**
 * Merchant brief registry (#3391): the code-owned, append-only home for
 * merchant-supplied brief fields (business description plus primary goal).
 *
 * Concept: `select.merchant-brief` (derives `evidence.context` plus
 * `select.store-goal`). Each field carries a closed `purpose`, a pinned
 * question stimulus per questionnaire version, and one zod answer schema.
 * Answers are validated at the Web write boundary and mirrored into
 * `org_merchant_attributes`; the registry is the source of truth, the table
 * only stores what the registry allowed.
 *
 * Append-only rule: never edit a field's question, purpose, options, or
 * schema in place. Wording changes mint a new `MERCHANT_BRIEF_QUESTIONNAIRE_VERSION`
 * with the old answers staying pinned to theirs.
 */
import * as z from 'zod';
import { merchantStoreGoalIdSchema, merchantStoreGoalIds, } from '@testfeed/integration-contracts';
export { merchantStoreGoalIdSchema, merchantStoreGoalIds };
/**
 * Closed five from `docs/concepts/select/store-goal.md` (canon; changing
 * them is a canon edit, not a code change). First code home for these ids.
 */
export const merchantStoreGoalLabels = {
    win_first_orders: 'Win first orders',
    bring_buyers_back: 'Bring buyers back',
    grow_basket: 'Grow basket size',
    hold_price: 'Hold price',
    stand_out: 'Stand out from alternatives',
};
export const MERCHANT_BRIEF_QUESTIONNAIRE_VERSION = 1;
export const merchantBriefQuestionKeys = ['business_description', 'primary_goal'];
export const merchantBriefQuestionKeySchema = z.enum(merchantBriefQuestionKeys);
/**
 * Closed usage vocabulary. Only `purpose` drives behavior; exploratory
 * labels, if ever added, are search-only and nothing branches on them.
 */
export const merchantBriefFieldPurposes = ['onboarding_gate', 'planner_input', 'display_only'];
export const merchantBriefFieldPurposeSchema = z.enum(merchantBriefFieldPurposes);
const businessDescriptionAnswerSchema = z.string().trim().min(10).max(2000);
const primaryGoalAnswerSchema = merchantStoreGoalIdSchema;
export const merchantBriefAnswerSchemas = {
    business_description: businessDescriptionAnswerSchema,
    primary_goal: primaryGoalAnswerSchema,
};
export const merchantBriefRegistry = {
    version: MERCHANT_BRIEF_QUESTIONNAIRE_VERSION,
    fields: {
        business_description: {
            key: 'business_description',
            purpose: 'planner_input',
            question: 'Describe your business in a sentence or two — what you sell and who it is for.',
            options: null,
        },
        primary_goal: {
            key: 'primary_goal',
            purpose: 'planner_input',
            question: 'What is the main outcome you want from testing?',
            options: merchantStoreGoalIds.map((id) => ({ id, label: merchantStoreGoalLabels[id] })),
        },
    },
};
/** Keys that must all be answered before the weave may run. Membership is by key, not purpose. */
export const MERCHANT_BRIEF_GATE_KEYS = ['business_description', 'primary_goal'];
/** Deterministic attribute id so re-answering the same version upserts instead of duplicating. */
export function merchantBriefAttributeId(orgId, key, version) {
    return `${orgId}:${key}:v${version}`;
}
/**
 * Parses one answer against the registry. Throws on unknown keys and invalid
 * answers — the write boundary must fail loud, never coerce or default.
 */
export function parseMerchantBriefAnswer(key, answer) {
    const parsedKey = merchantBriefQuestionKeySchema.parse(key);
    const schema = merchantBriefAnswerSchemas[parsedKey];
    const parsed = schema.parse(answer);
    return typeof parsed === 'string' ? parsed : String(parsed);
}
/** True when every gate key is present among the answered keys. */
export function isMerchantBriefGateComplete(answeredKeys) {
    const answered = new Set(answeredKeys);
    return MERCHANT_BRIEF_GATE_KEYS.every((key) => answered.has(key));
}
//# sourceMappingURL=merchant-brief.js.map